import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Card,
  Space,
  Typography,
  Spin,
  Select,
  Divider,
  Progress,
} from "antd";
import { DownloadOutlined, FileOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../database/firebase";
import CatalogPrint from "../../Components/CatalogPrint";
import { renderClassName } from "../../utils";
import { saveAs } from "file-saver";

const { Title, Text } = Typography;
const { Option } = Select;

function Export() {
  const [loading, setLoading] = useState(false);
  const [classeData, setClasseData] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedClassData, setSelectedClassData] = useState(null);
  const [display, setDisplay] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [completedClasses, setCompletedClasses] = useState(0);
  const [currentClass, setCurrentClass] = useState("");
  const [currentClassData, setCurrentClassData] = useState(null);
  const user = useSelector((state) => state.user);

  useEffect(() => {
    if (user.type === "admin") {
      fetchClasses();
    }
  }, [user]);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      // Fetch valid class IDs from clase document
      const claseSnapshot = await getDocs(collection(db, "clase"));
      const validClaseIds = [];
      claseSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.clase && Array.isArray(data.clase)) {
          data.clase.forEach((clasa) => {
            if (clasa.id) {
              validClaseIds.push(clasa.id);
            }
          });
        }
      });

      // Fetch class data from claseData collection
      const querySnapshot = await getDocs(collection(db, "claseData"));
      const classes = [];
      querySnapshot.forEach((doc) => {
        if (doc.id !== "faraclasa" && validClaseIds.includes(doc.id)) {
          classes.push({
            id: doc.id,
            ...doc.data(),
          });
        }
      });

      const sortedClasses = classes.sort((a, b) => {
        let clas = a;
        let clas2 = b;

        const nameA =
          clas?.anClasa === "Pregătitoare"
            ? clas?.anClasa + " " + clas?.identificator
            : "a " + clas?.anClasa + "-a" + " " + clas?.identificator;

        const nameB =
          clas2?.anClasa === "Pregătitoare"
            ? clas2?.anClasa + " " + clas2?.identificator
            : "a " + clas2?.anClasa + "-a" + " " + clas2?.identificator;

        if (nameA > nameB) return 1;
        else return -1;
      });

      setClasseData(sortedClasses);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
    setLoading(false);
  };

  const handleClassSelect = (classId) => {
    const classData = classeData?.find((c) => c.id === classId);
    setSelectedClass(classId);
    setSelectedClassData(classData);
  };

  const handleExportClass = () => {
    if (selectedClassData) {
      setDisplay(true);
    }
  };

  const exportAllCatalogs = () => {
    setIsExporting(true);
    setCompletedClasses(0);
    setCurrentClass("");
    setCurrentClassData(classeData[0]);
  };

  const onFileGenerated = (fileName, blob, pdfFileName, pdfBlob) => {
    if (fileName && blob) {
      saveAs(blob, fileName);
    }
    if (pdfFileName && pdfBlob) {
      saveAs(pdfBlob, pdfFileName);
    }

    const nextClassIndex = completedClasses + 1;
    if (nextClassIndex < classeData?.length) {
      setCompletedClasses(nextClassIndex);
      setCurrentClassData(classeData[nextClassIndex]);
    } else {
      setIsExporting(false);
      setCurrentClassData(null);
    }
  };

  useEffect(() => {
    if (isExporting && currentClassData) {
      const className = renderClassName(currentClassData);
      setCurrentClass(className);
    }
  }, [isExporting, currentClassData]);

  if (user.type !== "admin") {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Title level={3}>Acces interzis</Title>
        <Text>Această pagină este disponibilă doar pentru administratori.</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <Title level={2}>Export Cataloage</Title>
      <Text type="secondary">
        Exportă cataloagele tuturor claselor sau ale unei clase specifice
      </Text>

      <Divider />

      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card>
          <Title level={4}>Selectează clasa</Title>
          <Select
            style={{ width: "100%", marginBottom: "16px" }}
            placeholder="Selectează o clasă pentru previzualizare"
            loading={loading}
            onChange={handleClassSelect}
            value={selectedClass}
          >
            {classeData.map((classData) => (
              <Option key={classData.id} value={classData.id}>
                {classData && classData.anClasa
                  ? renderClassName(classData)
                  : `Clasa ${classData.id}`}
              </Option>
            ))}
          </Select>

          <Space>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportClass}
              disabled={!selectedClassData}
            >
              Exportă clasa selectată
            </Button>
            <Button
              icon={<FileOutlined />}
              onClick={exportAllCatalogs}
              disabled={classeData?.length === 0 || isExporting}
            >
              {isExporting ? "Export în curs..." : "Exportă toate cataloagele"}
            </Button>
          </Space>
        </Card>

        {display && selectedClassData && (
          <CatalogPrint
            classData={selectedClassData}
            mode="export"
            permision={true}
          />
        )}

        {isExporting && (
          <div>
            <Title level={3}>
              Export toate cataloagele -{" "}
              {process.env.REACT_APP_NAME || "Școala"}
            </Title>
            <Text>
              Se generează cataloagele... ({completedClasses}/{" "}
              {classeData?.length})
            </Text>
            <Progress
              percent={Math.round(
                (completedClasses / classeData?.length) * 100
              )}
              status="active"
              strokeColor={{
                from: "#108ee9",
                to: "#87d068",
              }}
            />
            {currentClass && <Text>Clasa curentă: {currentClass}</Text>}
            <Divider />
            {currentClassData && (
              <div style={{ display: "none" }}>
                <CatalogPrint
                  key={currentClassData.id}
                  classData={currentClassData}
                  mode="export-zip"
                  permision={true}
                  onFileGenerated={onFileGenerated}
                  className={renderClassName(currentClassData)}
                />
              </div>
            )}
          </div>
        )}
      </Space>

      {loading && (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
        </div>
      )}
    </div>
  );
}

export default Export;
